import { FMSCapabilityCell, FMSCapabilityKey, FMSCapabilityState, FMSObjectType, FMS_CAPABILITY_MATRIX } from "@swivl-mono/types";
import { Tooltip } from "@swivl-mono/ui";
import { useEffect } from "react";

import { NavBar } from "../../../Models/NavBar/NavBar.Model";
import { PROVIDER_LABELS, PROVIDER_ORDER } from "./JitFreshness.Shared";

const CAPABILITY_LABELS: Record<FMSCapabilityKey, string> = {
  [FMSCapabilityKey.FullPull]: "Full Pull",
  [FMSCapabilityKey.ChangesApi]: "Changes API",
  [FMSCapabilityKey.Webhooks]: "Webhooks",
  [FMSCapabilityKey.JitFetch]: "JIT",
  [FMSCapabilityKey.UpdatedAt]: "Updated At",
  [FMSCapabilityKey.NoteWrite]: "Note Write",
  [FMSCapabilityKey.AbandonedCartCandidacy]: "Cart Candidacy",
  [FMSCapabilityKey.InactiveLeaseStatusOptIn]: "Status Opt-In",
  [FMSCapabilityKey.JitChangesRefresh]: "JIT Changes Refresh"
};

const CAPABILITY_KEYS = Object.values(FMSCapabilityKey);
const OBJECT_TYPES = Object.values(FMSObjectType);

const STATE_LABELS: Record<FMSCapabilityState, string> = {
  [FMSCapabilityState.Implemented]: "Implemented",
  [FMSCapabilityState.Available]: "Available",
  [FMSCapabilityState.None]: "Not supported",
  [FMSCapabilityState.TBD]: "TBD"
};

const STATE_DESCRIPTIONS: Record<FMSCapabilityState, string> = {
  [FMSCapabilityState.Implemented]: "wired, drives behavior",
  [FMSCapabilityState.Available]: "FMS can do it, not yet wired",
  [FMSCapabilityState.None]: "not supported",
  [FMSCapabilityState.TBD]: "discovery pending"
};

// Distinct shape per state (not color-only): filled check / half-filled check /
// crossed ring / dashed ring. Colored via named tokens through `currentColor`.
function StateIcon({ state }: { state: FMSCapabilityState }) {
  const label = STATE_LABELS[state];
  switch (state) {
    case FMSCapabilityState.Implemented:
      return (
        <svg viewBox="0 0 20 20" role="img" aria-label={label} className="h-5 w-5 text-teal-500">
          <circle cx="10" cy="10" r="10" fill="currentColor" />
          <path d="M5.5 10.5l3 3 6-6.5" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case FMSCapabilityState.Available:
      return (
        <svg viewBox="0 0 20 20" role="img" aria-label={label} className="h-5 w-5 text-yellow-500">
          <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.2" />
          <path d="M10 1a9 9 0 0 0 0 18z" fill="currentColor" />
          <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M5.5 10.5l3 3 6-6.5" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case FMSCapabilityState.None:
      return (
        <svg viewBox="0 0 20 20" role="img" aria-label={label} className="h-5 w-5 text-red-500">
          <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M6.5 6.5l7 7M13.5 6.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case FMSCapabilityState.TBD:
    default:
      return (
        <svg viewBox="0 0 20 20" role="img" aria-label={label} className="h-5 w-5 text-grey-400">
          <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2.5 2.5" />
        </svg>
      );
  }
}

// Read-only admin view of the FMS Capability Matrix. The matrix is a static const in
// @swivl-mono/types — this page only displays it. Changing a value is a PR + deploy.
export function FMSCapabilitiesView() {
  useEffect(() => {
    NavBar.set(<NavBar.Component.Title title="FMS Capabilities" />, null, null, "FMS Capabilities");
  }, []);

  const providers = PROVIDER_ORDER.filter((fmsId) => FMS_CAPABILITY_MATRIX[fmsId]);

  return (
    // Plain block flow (no flex): flex children default to shrink, which squeezed the
    // matrix table into its own inner scrollbar instead of letting the page scroll.
    <div className="h-full overflow-auto p-6">
      <div className="mb-4">
        <h1 className="text-lg font-bold text-grey-900">FMS Capability Matrix</h1>
        <p className="mt-1 text-sm text-grey-700">
          Read-only. Source of truth is a static declaration in code — changing any value requires a pull request and deploy. The system reads only
          the
          <span className="font-bold text-grey-900"> Implemented</span> state; the rest are informational.
        </p>
        <Legend />
      </div>

      <div className="overflow-x-auto rounded-md border border-grey-200">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-grey-50 text-left text-grey-600">
              <th className="sticky left-0 z-10 bg-grey-50 px-4 py-2 font-semibold">FMS Provider</th>
              <th className="px-4 py-2 font-semibold">Object</th>
              {CAPABILITY_KEYS.map((capability) => (
                <th key={capability} className="px-4 py-2 text-center font-semibold">
                  {CAPABILITY_LABELS[capability]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {providers.map((fmsId) =>
              OBJECT_TYPES.map((objectType, objectIndex) => (
                <tr key={`${fmsId}-${objectType}`} className="border-t border-grey-200">
                  {objectIndex === 0 && (
                    <td
                      rowSpan={OBJECT_TYPES.length}
                      className="sticky left-0 z-10 border-r border-grey-200 bg-white px-4 py-2 align-top font-semibold text-grey-900"
                    >
                      {PROVIDER_LABELS[fmsId] ?? fmsId}
                    </td>
                  )}
                  <td className="px-4 py-2 text-grey-700">{objectType}</td>
                  {CAPABILITY_KEYS.map((capability) => (
                    <td key={capability} className="px-4 py-2">
                      <div className="flex justify-center">
                        <CapabilityCellView cell={FMS_CAPABILITY_MATRIX[fmsId]?.[objectType]?.[capability]} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface CapabilityCellViewProps {
  cell: FMSCapabilityCell | undefined;
}

function CapabilityCellView({ cell }: CapabilityCellViewProps) {
  const state = cell?.state ?? FMSCapabilityState.TBD;
  // A cell with a note shows a small dot marker; the note text appears on hover.
  const icon = (
    <span className="relative inline-flex">
      <StateIcon state={state} />
      {cell?.note && <span className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full bg-grey-500" />}
    </span>
  );

  if (cell?.note) {
    return <Tooltip content={cell.note}>{icon}</Tooltip>;
  }
  return icon;
}

function Legend() {
  const states: FMSCapabilityState[] = [
    FMSCapabilityState.Implemented,
    FMSCapabilityState.Available,
    FMSCapabilityState.None,
    FMSCapabilityState.TBD
  ];
  return (
    <div className="mt-3 flex flex-wrap gap-4 text-xs text-grey-600">
      {states.map((state) => (
        <span key={state} className="flex items-center gap-1.5">
          <StateIcon state={state} />
          <span className="font-medium text-grey-700">{STATE_LABELS[state]}</span>
          <span>— {STATE_DESCRIPTIONS[state]}</span>
        </span>
      ))}
    </div>
  );
}
